import { AnimatePresence, motion } from 'framer-motion';
import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Spacer } from '@/components/Flex';
import ExpandIconButton from '@/components/input/ExpandIconButton';
import { collapseVariant } from '@/styles/motion';
import { PaddingProps } from '@/types/ui';
import { getHorizontalPaddingStyle, getPaddingStyle } from '@/utils/style/ui';

interface StyleProps {
  padding?: PaddingProps;
}

interface Props
  extends React.ComponentPropsWithoutRef<'div'>,
    React.PropsWithChildren,
    StyleProps {
  isOpen?: boolean;
  isReadonly?: boolean;
  header?: React.ReactNode;
  icon?: React.ReactNode;
  onHeaderClick?: React.MouseEventHandler<HTMLDivElement>;
}

const Header = styled.div<StyleProps>`
  display: flex;
  width: 100%;
  ${({ theme, padding }) =>
    getPaddingStyle({ theme, padding, defaultValue: 'zero' })}
  user-select: none;
`;
const CollapseContainer = styled.div<StyleProps>`
  position: relative;
  ${({ theme, padding }) =>
    getHorizontalPaddingStyle({ theme, padding, defaultValue: 'zero' })}
`;
const Body = styled.div<StyleProps>`
  padding-bottom: ${({ theme, padding }) =>
    typeof padding === 'object'
      ? padding.y
        ? `${typeof padding.y === 'number' ? `${padding.y}px` : theme.size[padding.y ?? 'zero']}`
        : padding.bottom
          ? `${typeof padding.bottom === 'number' ? `${padding.bottom}px` : theme.size[padding.bottom ?? 'zero']}`
          : ''
      : typeof padding === 'number'
        ? `${padding}px`
        : theme.size[padding ?? 'zero']};
`;
const Container = styled.div`
  position: relative;
`;
const Root = styled.div<{ isReadonly?: boolean }>`
  ${Header} {
    cursor: ${({ isReadonly }) => (isReadonly ? 'default' : 'pointer')};
  }
`;

export default function Collapse({
  isOpen,
  isReadonly,
  padding,
  header,
  icon,
  children,
  onHeaderClick,
  ...props
}: Props) {
  // state
  const [innerIsOpen, setInnerIsOpen] = useState(false);

  // memo
  const isUsingProps = useMemo(() => isOpen !== undefined, [isOpen]);
  const usingIsOpen = useMemo(
    () => (isUsingProps ? isOpen : innerIsOpen),
    [innerIsOpen, isOpen, isUsingProps]
  );
  const expandIcon = useMemo(
    () => icon ?? <ExpandIconButton value={usingIsOpen} />,
    [icon, usingIsOpen]
  );

  // callback
  const handleHeaderClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) =>
      isReadonly
        ? undefined
        : isUsingProps
          ? onHeaderClick?.(event)
          : setInnerIsOpen((value) => !value),
    [isReadonly, isUsingProps, onHeaderClick]
  );

  return (
    <Root isReadonly={isReadonly} {...props}>
      <Header padding={padding} onClick={handleHeaderClick}>
        {header}
        {expandIcon ? (
          <>
            <Spacer />
            {icon ?? <ExpandIconButton value={usingIsOpen} />}
          </>
        ) : (
          ''
        )}
      </Header>
      <Container>
        <AnimatePresence>
          {usingIsOpen ? (
            <CollapseContainer
              initial="incoming"
              animate="active"
              exit="exit"
              key="collapse"
              as={motion.div}
              variants={collapseVariant}
              padding={padding}
            >
              <Body padding={padding}>{children}</Body>
            </CollapseContainer>
          ) : (
            ''
          )}
        </AnimatePresence>
      </Container>
    </Root>
  );
}
